/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Message } from 'primereact/message';
import PageSkeleton from '../../components/common/page-skeleton';
import { Dialog } from 'primereact/dialog';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { promptsService } from '../../services/prompts.service';
import { projectsService } from '../../services/projects.service';
import { aiModelsService } from '../../services/ai-models.service';
import { evalSuitesService } from '../../services/eval-suites.service';
import type { Prompt } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { ActionButton, StatusChip } from '../../components/common/panel';
import { Icon } from '../../components/common/icon';
import type { Tone } from '../../components/common/ui-kit';
import { useUserAuditNames } from '../../hooks/useUserLookup';
import { useCanUpdate, useCanDelete, useHasPermission } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import PromptRunPanel from '../../components/ai-runs/prompt-run-panel';
import PromptVersionsPanel from '../../components/prompts/prompt-versions-panel';
import {
  PromptDependenciesPanel,
  PromptRunHistoryPanel,
  PromptObservabilityPanel,
} from '../../components/prompts/prompt-insight-panels';
import EvalRunResult from '../../components/ai-runs/eval-run-result';
import { evalRunService } from '../../services/eval-run.service';
import { useNavigate, Link } from 'react-router-dom';
import { formatDate, formatTimestamp } from 'sharedUi/dateUtils';

/** Base64 URL-safe id encoding — matches useSecureNavigation so a built link
 *  lands on the same encoded view route the app navigates to internally. */
const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

const PromptView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'prompt',
    basePath: '/prompt-table',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.PROMPT_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.PROMPT_MASTER);
  const canExecute = useHasPermission(PERMISSIONS.PROMPT_EXECUTION_EXECUTE);
  const canReadRuns = useHasPermission(PERMISSIONS.EVAL_RUN_READ);
  const canReadExec = useHasPermission(PERMISSIONS.PROMPT_EXECUTION_READ);
  const navigate = useNavigate();

  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testRunOpen, setTestRunOpen] = useState(false);
  // Set when Test Run is for a draft version rather than the active one.
  const [runVersion, setRunVersion] = useState<number | undefined>(undefined);
  const [runDialogRef, setRunDialogRef] = useState<string | null>(null);
  // The section currently in view — highlighted in the "Prompt sections" nav so
  // it reads as a live table of contents, not a list of labels.
  const [activeSection, setActiveSection] = useState('');

  // This prompt's eval-run history (runs launched by its Test Run with runEvals).
  const { data: promptRunsResp } = useQuery({
    queryKey: ['eval-runs', 'by-prompt', prompt?._id],
    queryFn: () => evalRunService.listRuns({ promptRef: (prompt as any)!._id, pageSize: 10 }),
    enabled: !!prompt?._id && canReadRuns,
    staleTime: 30 * 1000,
  });
  const promptRuns = promptRunsResp?.data || [];

  // Lookups to resolve ids → human names (project, model, evals).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const { data: aiModelsResp } = useQuery({
    queryKey: ['ai-models', 'names'],
    queryFn: () => aiModelsService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const { data: evalsResp } = useQuery({
    queryKey: ['eval-suites', 'names'],
    queryFn: () => evalSuitesService.list({ page: 1, page_size: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  // The prompt's bound model is often outside the first page of the models list
  // (another project's scope, or past row 100), so the list lookup alone leaves
  // a raw ObjectId on screen. Resolve it directly by id as a fallback.
  const boundModelId = (prompt as any)?.modelId as string | undefined;
  const { data: boundModel } = useQuery({
    queryKey: ['ai-model', boundModelId],
    queryFn: () => aiModelsService.getById(boundModelId as string),
    enabled: !!boundModelId,
    staleTime: 5 * 60 * 1000,
  });
  const projectLabel = (appId?: string | null) => {
    if (!appId) return 'Global / Shared';
    const p = accessibleProjects.find((x) => x._id === appId);
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : appId;
  };
  const modelLabel = (mid?: string | null) => {
    if (!mid) return '-';
    const m = (aiModelsResp?.data || []).find((x) => x._id === mid);
    if (m) return m.displayName;
    if (boundModel && (boundModel as any)._id === mid) return boundModel.displayName;
    // The bound model is often in another project's scope, so neither the list
    // nor getById can resolve it. Fall back to the prompt's denormalised model
    // name (`llm`, backfilled from the model) rather than leaving a raw id.
    if (mid === boundModelId && (prompt as any)?.llm) return (prompt as any).llm;
    return mid;
  };
  const evalNames = ((prompt as any)?.evalIds || []).map((eid: string) => {
    const e = (evalsResp?.data || []).find((x) => x._id === eid);
    return e ? e.name : eid;
  });

  const { createdByName, updatedByName } = useUserAuditNames(prompt?.createdBy, prompt?.updatedBy);

  // Set breadcrumb title to show prompt name instead of UUID
  useBreadcrumbTitle(prompt?.name);


  useEffect(() => {
    // Wait for ID decryption to complete
    if (!isReady || !id) {
      return;
    }

    fetchPrompt(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  // Scrollspy: highlight the section the reader is currently in. Runs once the
  // prompt (and therefore its section anchors) are in the DOM.
  useEffect(() => {
    if (!prompt) return;
    const els = Array.from(document.querySelectorAll('[id^="sec-"]')) as HTMLElement[];
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      // Offset the top for the sticky bar; shrink the bottom so the active
      // section is the one near the top of the viewport, not just anywhere on it.
      { rootMargin: '-80px 0px -60% 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [prompt?._id]);

  async function fetchPrompt(showSkeleton = false) {
    if (!id) return;
    try {
      if (showSkeleton) setLoading(true);
      setPrompt(await promptsService.getById(id));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to fetch prompt details'));
    } finally {
      setLoading(false);
    }
  }

  // Handlers
  const handleEdit = () => id && navigateToEdit(id);
  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await promptsService.softDelete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete prompt'));
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  // Loading state
  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  // Error state
  if (error || !prompt) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Prompt not found'} className="mb-4 w-full" />
        <ActionButton kind="secondary" icon="arrow-left" label="Back to Prompts" onClick={() => navigateToList()} />
      </div>
    );
  }

  const isDeleted = !!((prompt as any).isDelete || prompt.isDeleted);
  const modelName = (prompt as any).modelId ? modelLabel((prompt as any).modelId) : prompt.llm;
  const asText = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));
  // A timestamp with its actor underneath; either half may be missing.
  const stamp = (at: unknown, by: string) => {
    const when = at ? formatTimestamp(at as string) : '';
    const who = by && by !== '-' ? by : '';
    if (!when && !who) return '–';
    return (<>{when && <span className="tabular-nums">{when}</span>}{who && <span className="block text-xs text-ink-muted">{who}</span>}</>);
  };
  const runTone = (s: string): Tone => (s === 'completed' ? 'success'
    : s === 'partial' || s === 'running' ? 'warn'
    : s === 'failed' || s === 'cancelled' ? 'danger' : 'info');

  // Smooth-scroll a section into view from the jump nav, and flash it briefly so
  // it is obvious where you landed after the scroll.
  const scrollToId = (sid: string) => {
    const el = document.getElementById(sid);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(sid);
    el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2'), 1200);
  };

  // The jump nav lists only the sections actually present, in page order.
  const navItems = [
    (prompt.systemRole || prompt.objective) && { id: 'sec-role', label: 'Role & objective' },
    prompt.taskInstructions && { id: 'sec-instructions', label: 'Task instructions' },
    (prompt.taskInput || prompt.taskOutputFormat) && { id: 'sec-io', label: 'Input & output' },
    prompt.taskExample && { id: 'sec-example', label: 'Task example' },
    prompt.settingsJson && { id: 'sec-settings', label: 'Settings' },
    id && { id: 'sec-used-by', label: 'Used by' },
    canReadRuns && { id: 'sec-eval-runs', label: 'Eval run history' },
    canReadExec && { id: 'sec-run-history', label: 'Run history' },
    canReadExec && { id: 'sec-observability', label: 'Observability' },
    id && { id: 'sec-versions', label: 'Versions' },
  ].filter(Boolean) as { id: string; label: string }[];

  return (
    <div className="mx-auto max-w-[1280px] px-3 pb-10 pt-2 sm:px-5" data-testid="SCR-Prompt-View">
      <button type="button" onClick={() => navigateToList()} className="pd-back ui-press">
        <Icon name="arrow-left" size={14} /> Prompts
      </button>

      {/* Header: the prompt is the title; one status, one primary action. */}
      <header className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="text-title font-semibold text-ink" style={{ textWrap: 'balance' }}>{prompt.name}</h1>
            <StatusChip tone="success" testId="PromptView.ActiveVersion">{`v${prompt.version ?? 1} · Active version`}</StatusChip>
            {prompt.draftVersion != null && (
              <StatusChip tone="warn" testId="PromptView.DraftVersion">{`Draft v${prompt.draftVersion} in progress`}</StatusChip>
            )}
          </div>
          {prompt.aiRole && <p className="mt-1 text-body-sm text-ink-muted">{prompt.aiRole}</p>}
        </div>

        {!isDeleted && (canExecute || canUpdate || canDelete) && (
          <div className="flex shrink-0 items-center gap-2">
            {canDelete && <ActionButton kind="danger" icon="trash" label="Delete" onClick={handleDeleteClick} />}
            {canUpdate && <ActionButton kind="secondary" icon="pencil" label="Edit" onClick={handleEdit} />}
            {canExecute && <ActionButton kind="primary" icon="play" label="Test run" onClick={() => setTestRunOpen(true)} testId="PromptView.Button.TestRun" />}
          </div>
        )}
      </header>

      <div className="mt-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_21rem]">
        {/* Main column: the prompt reads top to bottom, as the model sees it. */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* Role & objective — what the model is, and what it is for. */}
          {(prompt.systemRole || prompt.objective) && (
            <section id="sec-role" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Role &amp; objective</h2>
              <div className="mt-4 flex flex-col gap-5">
                {prompt.systemRole && (
                  <div><span className="pd-label">System role</span><p className="pd-text">{prompt.systemRole}</p></div>
                )}
                {prompt.objective && (
                  <div><span className="pd-label">Objective</span><p className="pd-text">{prompt.objective}</p></div>
                )}
              </div>
            </section>
          )}

          {/* Task instructions — the standing task the model follows. */}
          {prompt.taskInstructions && (
            <section id="sec-instructions" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Task instructions</h2>
              <pre className="ui-inset pd-well font-sans mt-4">{prompt.taskInstructions}</pre>
            </section>
          )}

          {/* Input & output — the contract: what goes in, what must come back. */}
          {(prompt.taskInput || prompt.taskOutputFormat) && (
            <section id="sec-io" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Input &amp; output</h2>
              <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
                {prompt.taskInput && (
                  <div className="min-w-0"><span className="pd-label">Task input</span><pre className="ui-inset pd-well pd-well--mono">{prompt.taskInput}</pre></div>
                )}
                {prompt.taskOutputFormat && (
                  <div className="min-w-0"><span className="pd-label">Output format</span><pre className="ui-inset pd-well font-sans">{prompt.taskOutputFormat}</pre></div>
                )}
              </div>
            </section>
          )}

          {/* Task example — a worked input/output pair. */}
          {prompt.taskExample && (
            <section id="sec-example" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Task example</h2>
              <pre className="ui-inset pd-well pd-well--mono mt-4">{asText(prompt.taskExample)}</pre>
            </section>
          )}

          {/* Settings — provider / sampling overrides stored with the prompt. */}
          {prompt.settingsJson && (
            <section id="sec-settings" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Settings</h2>
              <pre className="ui-inset pd-well pd-well--mono mt-4">{asText(prompt.settingsJson)}</pre>
            </section>
          )}

          {/* Nothing authored yet. */}
          {!prompt.systemRole && !prompt.objective && !prompt.taskInstructions && !prompt.taskInput
            && !prompt.taskOutputFormat && !prompt.taskExample && !prompt.settingsJson && (
            <section className="ui-panel p-5">
              <p className="text-sm text-ink-muted">This prompt has no text yet.{canUpdate ? ' Use Edit to write it.' : ''}</p>
            </section>
          )}

        {/* Dependency layer — what this prompt needs (upstream) and what embeds
            or targets it (downstream). */}
        {id && <div id="sec-used-by" className="scroll-mt-20"><PromptDependenciesPanel promptId={id} /></div>}

        {/* Eval Run History — runs this prompt launched via Test Run + Run evals */}
        {canReadRuns && (
          <section id="sec-eval-runs" className="ui-panel p-5 scroll-mt-20">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink">Eval Run History</h2>
              {promptRuns.length > 0 && (
                <ActionButton kind="quiet" size="sm" label="View all"
                  onClick={() => navigate(`/eval-runs?promptRef=${(prompt as any)._id}`)} />
              )}
            </div>
            {promptRuns.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">
                No eval runs yet. Use <span className="font-medium">Test run</span> with “Run evals” checked.
              </p>
            ) : (
              <ul className="mt-3 flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
                {promptRuns.map((r: any) => {
                  const pr = r.summary?.passRate;
                  return (
                    <li key={r._id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-ink">
                          {r.suiteCode || r.suiteRef || 'Eval suite'}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                          <span className="truncate font-mono">{r.evalRunId || r._id}</span>
                          <span>·</span>
                          <span className="whitespace-nowrap">{formatDate(r.runDateTime || r.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="hidden text-xs tabular-nums text-ink-muted sm:inline">
                          {r.summary?.passed ?? '–'}/{r.summary?.total ?? '–'} passed
                        </span>
                        <span className="w-10 text-right text-sm font-semibold tabular-nums text-ink">
                          {pr === null || pr === undefined ? '–' : `${(pr * 100).toFixed(0)}%`}
                        </span>
                        <StatusChip tone={runTone(r.status)}>{r.status}</StatusChip>
                        <ActionButton kind="quiet" size="sm" label="View" onClick={() => setRunDialogRef(r._id)} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* Prompt run ledger — this prompt's own executions (cost, tokens,
            latency, status), separate from the Eval Run History above. */}
        {id && canReadExec && (
          <div id="sec-run-history" className="scroll-mt-20"><PromptRunHistoryPanel promptId={id} /></div>
        )}

        {/* Observability — health summary over this prompt's traces, each
            openable in full, with a deep-link to AI Observability. */}
        {id && canReadExec && (
          <div id="sec-observability" className="scroll-mt-20"><PromptObservabilityPanel promptId={id} /></div>
        )}

          {id && (
            <div id="sec-versions" className="scroll-mt-20">
              <PromptVersionsPanel
                promptId={id}
                canUpdate={canUpdate && !isDeleted}
                onChanged={() => fetchPrompt()}
                onRunDraft={canExecute ? (v) => { setRunVersion(v); setTestRunOpen(true); } : undefined}
              />
            </div>
          )}
        </div>

        {/* Side rail: everything about the prompt that is not the prompt. */}
        <aside className="ui-panel p-5 lg:sticky lg:top-4">
          <h2 className="text-sm font-semibold text-ink">Details</h2>
          <dl className="pd-meta mt-4">
            <dt>Type</dt><dd>{prompt.type || '–'}</dd>
            <dt>Model</dt>
            <dd>
              {(prompt as any).modelId
                ? <Link to={`/ai-models/${encodeId((prompt as any).modelId)}`} className="text-blue-600 hover:underline">{modelName || '–'}</Link>
                : (modelName || '–')}
            </dd>
            <dt>Project</dt>
            <dd>
              {(prompt as any).appId
                ? <Link to={`/projects/${encodeId((prompt as any).appId)}`} className="text-blue-600 hover:underline">{projectLabel((prompt as any).appId)}</Link>
                : projectLabel((prompt as any).appId)}
            </dd>
            <dt>Technology</dt><dd>{prompt.tech || '–'}</dd>
            <dt>Evals</dt>
            <dd>
              {evalNames.length > 0 ? (
                <span className="flex flex-wrap gap-1.5">
                  {evalNames.map((n: string, i: number) => {
                    const eid = ((prompt as any).evalIds || [])[i];
                    const chip = <StatusChip tone="neutral" dot={false}>{n}</StatusChip>;
                    return eid
                      ? <Link key={eid} to={`/evals/${encodeId(eid)}`} className="no-underline">{chip}</Link>
                      : <span key={i}>{chip}</span>;
                  })}
                </span>
              ) : '–'}
            </dd>
            {prompt.iconPath && (<><dt>Icon</dt><dd className="font-mono text-xs">{prompt.iconPath}</dd></>)}
          </dl>
          <hr className="pd-rule my-5" />
          <dl className="pd-meta">
            <dt>Created</dt><dd>{stamp((prompt as any).createdAt, createdByName)}</dd>
            <dt>Updated</dt><dd>{stamp((prompt as any).updatedAt, updatedByName)}</dd>
          </dl>

          {/* Jump nav — land on a section without scrolling the whole page. */}
          {navItems.length > 0 && (
            <>
              <hr className="pd-rule my-5" />
              <nav aria-label="Prompt sections">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Prompt sections</h3>
                <ul className="flex flex-col gap-0.5">
                  {navItems.map((it) => {
                    const active = activeSection === it.id;
                    return (
                      <li key={it.id}>
                        <button type="button" onClick={() => scrollToId(it.id)}
                          aria-current={active ? 'true' : undefined}
                          className={`flex w-full items-center gap-1.5 border-l-2 py-1 pl-2 pr-2 text-left text-sm transition-colors ${
                            active
                              ? 'border-blue-600 font-medium text-blue-600'
                              : 'border-transparent text-ink-muted hover:border-gray-300 hover:text-blue-600'
                          }`}>
                          <span className="text-xs opacity-60">›</span>
                          {it.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </>
          )}
        </aside>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        visible={deleteDialogOpen}
        onCancel={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        message="Are you sure you want to delete this prompt? This is a soft delete and the data will be preserved but marked as deleted."
        testId="PromptView.Dialog.Delete"
      />

      {/* Test Run Dialog */}
      <Dialog
        header={`Test Run — ${prompt.name}${runVersion ? ` (draft v${runVersion})` : ` (v${prompt.version ?? 1})`}`}
        visible={testRunOpen}
        onHide={() => { setTestRunOpen(false); setRunVersion(undefined); }}
        style={{ width: '48rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
        data-testid="PromptView.Dialog.TestRun"
      >
        {id && <PromptRunPanel key={runVersion ?? 'active'} promptId={id} version={runVersion}
                               promptCode={prompt.name} activeVersion={prompt.version ?? 1} />}
      </Dialog>

      {/* Eval Run detail */}
      <Dialog
        header="Eval Run"
        visible={!!runDialogRef}
        onHide={() => setRunDialogRef(null)}
        style={{ width: '48rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
      >
        {runDialogRef && <EvalRunResult runRef={runDialogRef} />}
      </Dialog>
    </div>
  );
};

export default PromptView;
